import {
  watchMyProperties,
  watchAttributes,
  ensureDefaultAttributes,
  watchNotes,
  migrateAttributeStyle,
  watchAccountShares,
  getPropertiesByIds,
  accountMemberNames,
  getAccountShareLink,
} from '../lib/firestore.js';
import { createFromInput } from '../lib/create.js';
import { NUMBER, displayLabel, recentAttributes, displayValue } from '../lib/attrs.js';
import { fileId, propertyStats } from '../lib/stats.js';
import { STATUS_LABELS, escapeHtml, escapeAttr, sourceLinkHtml, renderHeaderIdentity, isIOS } from './parts.js';

// iOS shortcut link — the only entry to the #/add passthrough.
const IOS_SHORTCUT_URL = 'https://www.icloud.com/shortcuts/17900e982e744a5dbe6ace05a84b51f8';
const DL_ICON = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>`;
const COMMENT_ICON = `<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>`;
const FILE_ICON = `<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"/><polyline points="13 2 13 9 20 9"/></svg>`;
const ATTRS_ICON = `<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.83z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>`;
const SORT_ICON = `<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="7" y1="3" x2="7" y2="21"/><polyline points="11 17 7 21 3 17"/><line x1="17" y1="3" x2="17" y2="21"/><polyline points="13 7 17 3 21 7"/></svg>`;
const FAB_PLUS = `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>`;
const FAB_CLOSE = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>`;

// Default list order: status flow (rejected always last), then interaction
// count (files + comments) desc. Stable sort keeps Firestore's newest-first
// order within ties.
const STATUS_ORDER = ['shortlist', 'reviewing', 'potential', 'purchased', 'rejected'];
// Sort menu options — the active key is reflected in each row's meta.
const SORT_OPTIONS = [
  ['status', 'Sort: Status'],
  ['interactions', 'Sort: Interactions'],
  ['created', 'Sort: Created'],
  ['attributes', 'Sort: Attribute count'],
];
// Anything not in this set is a user-defined attribute id (sorted by value).
const FIXED_SORTS = new Set(SORT_OPTIONS.map(([k]) => k));

/** One row stat: icon + total, with a "+N" accent when new since last view.
 * Zero counts are omitted entirely (no "0" icon). */
function statHtml(icon, total, nnew, label) {
  if (!total && !nnew) return '';
  const title = nnew ? `${label}: ${total}, ${nnew} new` : `${label}: ${total}`;
  return `<span class="pstat" title="${escapeAttr(title)}">${icon}${total}${nnew ? `<b class="pstat-new">+${nnew}</b>` : ''}</span>`;
}

// Statuses available as a filter (the "all" pseudo-tab lives on as no filter).
const STATUS_OPTIONS = [
  ['potential', 'Potential'],
  ['reviewing', 'Reviewing'],
  ['shortlist', 'Shortlist'],
  ['rejected', 'Rejected'],
  ['purchased', 'Purchased'],
];

export function renderDashboard(el, { user, navigate }) {
  let props = [];
  let attrDefs = [];
  let filters = []; // [{ kind: 'status'|'attr', name?, op, value }] — ANDed
  let unsubAttrs = null;
  let sortBy = 'status'; // active sort key (SORT_OPTIONS); reflected in row meta
  // Account sharing (S3): the merged portfolio = mine + partners' (readable
  // via the materialized memberships). Tabs filter; stopped partners keep a
  // note instead of their (now unreadable) properties.
  let myProps = [];
  let partnerProps = [];
  let partnerNames = {};    // partner uid → display name (creator chips)
  let stoppedPartners = []; // partners whose account share is inactive
  let tab = 'all';

  // ── per-property stats: files + comments, "new since last view" ────────
  // Baseline snapshot of seen file/note ids persists to localStorage; on
  // leave (route change / tab hide) the current view becomes the baseline,
  // so the next visit's deltas are relative to this one. First visit counts
  // nothing as new (no "+N new" noise on an empty history).
  const statsKey = `ps.stats.${user.uid}`;
  let firstVisit = false;
  let baseline = { files: {}, notes: {} };
  try {
    const raw = localStorage.getItem(statsKey);
    if (raw) baseline = JSON.parse(raw);
    else firstVisit = true;
  } catch { /* private mode / quota — treat as first visit */ }
  let noteUnsubs = new Map();   // property id → unsubscribe
  let notesByPid = new Map();   // property id → Set(note ids)

  el.innerHTML = `
    <div class="app-header">
      <a class="brand" href="#/map">Property Source</a>
      <nav><a href="#/map">Map</a></nav>
      <div id="header-who"></div>
    </div>
    ${user.isAnonymous && !localStorage.getItem('guest-banner-dismissed') ? `
    <div class="guest-banner" id="guest-banner">
      <span>Guest mode: your list lives on this device — <a href="#/login?next=dashboard">sign in to keep it</a> and unlock extraction.</span>
      <button class="banner-dismiss" id="dismiss-banner" aria-label="Dismiss">×</button>
    </div>` : ''}
    <main>
      <div class="dash-actions" id="dash-actions">
        <form class="add-inline" id="add-form">
          <input type="text" id="add-input" placeholder="Paste a Trade Me URL, or type an address / name"
            aria-label="Add a property" autocomplete="off" />
          <button class="btn" id="add-btn" type="submit">Add</button>
        </form>
        ${isIOS() ? `<a class="btn ghost" href="${IOS_SHORTCUT_URL}">iOS ${DL_ICON}</a>` : ''}
        <p class="muted" id="add-hint" style="flex-basis:100%;margin:0" hidden></p>
      </div>
      <!-- Mobile: the row above collapses into this FAB (top right). -->
      <button class="fab" id="fab" aria-label="Add property" aria-expanded="false">${FAB_PLUS}</button>
      <div class="tabs" id="dash-tabs">
        <button class="tab active" data-tab="all">All</button>
        <button class="tab" data-tab="mine">Mine</button>
        <button class="tab" data-tab="shared">Shared</button>
      </div>
      <div class="filter-bar" id="filter-bar">
        <select id="f-sort" aria-label="Sort by"></select>
        <select id="f-add" aria-label="Add filter"></select>
        <div id="f-conditions"></div>
      </div>
      <div class="card" id="props"></div>
    </main>`;

  renderHeaderIdentity(el.querySelector('#header-who'), { user, navigate });

  // Inline create — same flow as the old add page, no navigation away.
  const addForm = el.querySelector('#add-form');
  const addInput = el.querySelector('#add-input');
  const addBtn = el.querySelector('#add-btn');
  const addHint = el.querySelector('#add-hint');
  addForm.addEventListener('submit', async (e) => {
    e.preventDefault();
    const raw = addInput.value.trim();
    if (!raw) return;
    addBtn.disabled = true;
    addHint.hidden = true;
    try {
      const res = await createFromInput(user, raw);
      if (res.dup) {
        navigate(`#/map?pid=${res.dup.id}`);
        return;
      }
      addInput.value = '';
      addHint.textContent = user.isAnonymous
        ? 'Added ✓ — sign in to fetch listing details'
        : 'Added ✓';
      addHint.hidden = false;
      setTimeout(() => (addHint.hidden = true), 3000);
      addBtn.disabled = false;
      addInput.focus();
    } catch (err) {
      addHint.textContent = err.message;
      addHint.hidden = false;
      addBtn.disabled = false;
    }
  });

  el.querySelector('#dismiss-banner')?.addEventListener('click', () => {
    localStorage.setItem('guest-banner-dismissed', '1');
    el.querySelector('#guest-banner')?.remove();
  });
  const listEl = el.querySelector('#props');
  const filterEl = el.querySelector('#filter-bar');

  // ── sort menu — the active key is echoed in each row's meta line ────────
  // Options: the fixed keys plus every defined attribute (sorted by value).
  const sortEl = filterEl.querySelector('#f-sort');
  // Attribute usage (name → count of properties with a value) — feeds both
  // the filter menu and the sort menu so only used attributes appear.
  const attributeUsage = () => {
    const usage = new Map();
    for (const p of props) {
      for (const k of Object.keys(p.user_attributes || {})) usage.set(k, (usage.get(k) || 0) + 1);
    }
    return usage;
  };

  function renderSortSelect() {
    // Only attributes actually used by properties, each with a property count.
    const usedAttrs = [...attributeUsage().entries()].sort((a, b) => b[1] - a[1]);
    const valid = new Set([...SORT_OPTIONS.map(([k]) => k), ...usedAttrs.map(([name]) => name)]);
    if (!valid.has(sortBy)) sortBy = 'status'; // unused/deleted attribute → default
    sortEl.innerHTML = [
      ...SORT_OPTIONS.map(([k, label]) => `<option value="${k}" ${sortBy === k ? 'selected' : ''}>${label}</option>`),
      ...usedAttrs.map(([name, count]) => `<option value="${escapeAttr(name)}" ${sortBy === name ? 'selected' : ''}>Sort: ${escapeHtml(displayLabel(name))} (${count})</option>`),
    ].join('');
  }
  sortEl.addEventListener('change', (e) => {
    sortBy = e.target.value;
    renderList();
  });
  renderSortSelect();

  // ── per-property stats — icon + count, deltas vs the last-view baseline ─
  /** Watch the notes thread of every visible property (keyed by pid — S4:
   * notes live on the canonical doc, not the share token). New pids start a
   * watch, gone ones stop theirs; comment ids feed the row icons. */
  function syncNoteWatchers() {
    const pids = new Set(props.map((p) => p.id));
    for (const [pid, unsub] of noteUnsubs) {
      if (!pids.has(pid)) {
        unsub();
        noteUnsubs.delete(pid);
        notesByPid.delete(pid);
      }
    }
    for (const pid of pids) {
      if (noteUnsubs.has(pid)) continue;
      const seen = new Set();
      notesByPid.set(pid, seen);
      noteUnsubs.set(pid, watchNotes(pid, (notes) => {
        seen.clear();
        for (const n of notes) seen.add(n.id);
        renderList();
      }));
    }
  }

  /** The current view's ids — saved as the next visit's baseline. */
  function currentSnapshot() {
    const files = {};
    for (const p of props) files[p.id] = (p.files || []).map(fileId).filter(Boolean);
    const notes = {};
    for (const [pid, seen] of notesByPid) notes[pid] = [...seen];
    return { files, notes };
  }

  /** Persist the "seen" baseline when the user leaves the dashboard — the
   * next visit's deltas compare against it. */
  function saveBaseline() {
    try {
      localStorage.setItem(statsKey, JSON.stringify(currentSnapshot()));
    } catch { /* quota/private mode — the stats just restart next visit */ }
  }

  // ── "Filter by:" — status + attributes, multiple conditions ANDed ───────
  function renderFilterBar() {
    const addEl = filterEl.querySelector('#f-add');
    const condEl = filterEl.querySelector('#f-conditions');
    const hasStatus = filters.some((f) => f.kind === 'status');
    // Only attributes actually used by properties, each with a property count.
    const usedAttrs = [...attributeUsage().entries()].sort((a, b) => b[1] - a[1]);
    addEl.innerHTML = `<option value="">Add filter…</option>` +
      (hasStatus ? '' : `<option value="__status__">Status</option>`) +
      usedAttrs.map(([name, count]) => `<option value="${escapeAttr(name)}">${escapeHtml(displayLabel(name))} (${count})</option>`).join('');

    condEl.innerHTML = filters.map((f, i) => {
      if (f.kind === 'status') {
        return `<span class="f-chip" data-i="${i}">
          <span class="f-name">Status</span>
          <select class="f-status" data-i="${i}" aria-label="Status">
            ${STATUS_OPTIONS.map(([k, label]) => `<option value="${k}" ${f.value === k ? 'selected' : ''}>${label}</option>`).join('')}
          </select>
          <button class="f-x" data-i="${i}" aria-label="Remove filter">×</button>
        </span>`;
      }
      const def = attrDefs.find((d) => d.id === f.name);
      const numeric = def?.type === NUMBER;
      return `<span class="f-chip" data-i="${i}">
        <span class="f-name">${escapeHtml(displayLabel(f.name))}</span>
        <select class="f-op" data-i="${i}" aria-label="Operator">
          <option value="eq" ${f.op === 'eq' ? 'selected' : ''}>=</option>
          <option value="ge" ${f.op === 'ge' ? 'selected' : ''} ${numeric ? '' : 'hidden'}>≥</option>
          <option value="le" ${f.op === 'le' ? 'selected' : ''} ${numeric ? '' : 'hidden'}>≤</option>
        </select>
        <span class="f-value-wrap">
          <input class="f-value" data-i="${i}" type="text" placeholder="Value" value="${escapeAttr(f.value ?? '')}" aria-label="Value" autocomplete="off" />
          <div class="f-suggest" hidden></div>
        </span>
        <button class="f-x" data-i="${i}" aria-label="Remove filter">×</button>
      </span>`;
    }).join('');
    condEl.hidden = !filters.length;

    addEl.addEventListener('change', () => {
      const v = addEl.value;
      addEl.value = '';
      if (!v) return;
      if (v === '__status__') filters.push({ kind: 'status', value: STATUS_OPTIONS[0][0] });
      else filters.push({ kind: 'attr', name: v, op: 'eq', value: '' });
      renderFilterBar();
      renderList();
    });
    condEl.querySelectorAll('.f-status').forEach((s) =>
      s.addEventListener('change', (e) => {
        filters[Number(e.target.dataset.i)].value = e.target.value;
        renderList();
      }),
    );
    condEl.querySelectorAll('.f-op').forEach((s) =>
      s.addEventListener('change', (e) => {
        filters[Number(e.target.dataset.i)].op = e.target.value;
        renderList();
      }),
    );
    // Value inputs get a custom suggestion dropdown that opens on first focus
    // (native datalists don't — same pattern as the property page's attrs).
    condEl.querySelectorAll('.f-value').forEach((inp) => {
      const i = Number(inp.dataset.i);
      const chip = inp.closest('.f-chip');
      const suggest = chip.querySelector('.f-suggest');
      const knownValues = () => Object.keys(attrDefs.find((d) => d.id === filters[i].name)?.values || {});
      const showSuggest = () => {
        const q = (filters[i].value || '').toLowerCase();
        const matches = knownValues().filter((v) => !q || v.toLowerCase().includes(q));
        if (!matches.length) {
          suggest.hidden = true;
          return;
        }
        suggest.hidden = false;
        suggest.innerHTML = matches
          .map((v) => `<button type="button" data-v="${escapeAttr(v)}">${escapeHtml(displayLabel(v))}</button>`)
          .join('');
        suggest.querySelectorAll('button').forEach((b) => {
          // Keep the input focused so blur doesn't hide the list before click.
          b.addEventListener('mousedown', (e) => e.preventDefault());
          b.addEventListener('click', () => {
            filters[i].value = b.dataset.v;
            inp.value = b.dataset.v;
            suggest.hidden = true;
            renderList();
          });
        });
      };
      inp.addEventListener('focus', showSuggest);
      inp.addEventListener('input', (e) => {
        filters[i].value = e.target.value;
        renderList();
        showSuggest();
      });
      inp.addEventListener('blur', () => {
        suggest.hidden = true;
      });
    });
    condEl.querySelectorAll('.f-x').forEach((b) =>
      b.addEventListener('click', (e) => {
        filters.splice(Number(e.target.dataset.i), 1);
        renderFilterBar();
        renderList();
      }),
    );
  }

  /** Forgiving match: equals numerically OR as a case-insensitive string;
   * ranges only for numbers. Properties without the attribute don't match.
   * An attribute condition with no value yet is inert. */
  function matchesFilters(p) {
    return filters.every((f) => {
      if (f.kind === 'status') return p.status === f.value;
      if (!f.value) return true;
      const v = p.user_attributes?.[f.name];
      if (v === undefined) return false;
      const sv = String(v).trim();
      const fv = String(f.value);
      if (f.op === 'eq') {
        return sv.toLowerCase() === fv.trim().toLowerCase()
          || (sv !== '' && Number(v) === Number(fv));
      }
      const nv = Number(v);
      const nf = Number(fv);
      return Number.isFinite(nv) && Number.isFinite(nf)
        && (f.op === 'ge' ? nv >= nf : nv <= nf);
    });
  }

  function renderList() {
    const interactions = (p) =>
      (p.files || []).length + (notesByPid.get(p.id)?.size || 0);
    const attrCount = (p) => Object.keys(p.user_attributes || {}).length;
    const tsOf = (t) => (t && typeof t.toMillis === 'function' ? t.toMillis() : t ? new Date(t).getTime() : 0);
    const tabProps = tab === 'mine'
      ? props.filter((p) => p.owner_uid === user.uid)
      : tab === 'shared'
        ? props.filter((p) => p.owner_uid !== user.uid)
        : props;
    const visible = tabProps
      .filter(matchesFilters)
      .sort((a, b) => {
        // Primary key per the sort menu; status flow (rejected last) is the
        // tiebreak everywhere, and interactions tiebreak the status sort.
        if (sortBy === 'interactions') {
          const ia = interactions(a);
          const ib = interactions(b);
          if (ib !== ia) return ib - ia;
        } else if (sortBy === 'created') {
          const ta = tsOf(a.created_at);
          const tb = tsOf(b.created_at);
          if (tb !== ta) return tb - ta;
        } else if (sortBy === 'attributes') {
          const aa = attrCount(a);
          const ab = attrCount(b);
          if (ab !== aa) return ab - aa;
        } else if (!FIXED_SORTS.has(sortBy)) {
          // Sort by an attribute value: properties with a value first, then
          // numbers desc (declared NUMBER defs sort numerically even when the
          // stored value is a string) / strings asc.
          const def = attrDefs.find((d) => d.id === sortBy);
          const va = a.user_attributes?.[sortBy];
          const vb = b.user_attributes?.[sortBy];
          const hasA = va !== undefined && va !== null && va !== '';
          const hasB = vb !== undefined && vb !== null && vb !== '';
          if (hasA !== hasB) return hasA ? -1 : 1;
          if (hasA) {
            if (def?.type === NUMBER || (typeof va === 'number' && typeof vb === 'number')) {
              const na = Number(va);
              const nb = Number(vb);
              if (nb !== na) return nb - na;
            } else {
              const sa = String(va).toLowerCase();
              const sb = String(vb).toLowerCase();
              if (sa !== sb) return sa < sb ? -1 : 1;
            }
          }
        }
        const sa = STATUS_ORDER.indexOf(a.status);
        const sb = STATUS_ORDER.indexOf(b.status);
        if (sa !== sb) return sa - sb;
        if (sortBy === 'status') {
          const ia = interactions(a);
          const ib = interactions(b);
          if (ib !== ia) return ib - ia;
        }
        return 0; // stable sort keeps creation order within ties
      });
    // The meta leads with the value the list is sorted by (status shows via
    // the pill; the other keys get an explicit marker).
    const sortValueHtml = (p, c) => {
      if (sortBy === 'created') {
        const ms = tsOf(p.created_at);
        return ms
          ? `<span class="sort-value" title="Sorted by created">${SORT_ICON}${escapeHtml(new Date(ms).toLocaleDateString('en-NZ', { day: 'numeric', month: 'short' }))}</span>`
          : '';
      }
      if (sortBy === 'interactions') {
        const v = c.comments + c.files;
        return v ? `<span class="sort-value" title="Sorted by interactions">${SORT_ICON}${v}</span>` : '';
      }
      if (sortBy === 'attributes') {
        const v = attrCount(p);
        return v ? `<span class="sort-value" title="Sorted by attribute count">${SORT_ICON}${v}</span>` : '';
      }
      if (!FIXED_SORTS.has(sortBy)) {
        const v = p.user_attributes?.[sortBy];
        return v === undefined || v === null || v === ''
          ? ''
          : `<span class="sort-value" title="Sorted by ${escapeAttr(displayLabel(sortBy))}">${SORT_ICON}${escapeHtml(displayLabel(v))}</span>`;
      }
      return ''; // status — the pill already shows it
    };
    if (!visible.length) {
      listEl.innerHTML = filters.length
        ? '<div class="empty">No properties match this filter.<br><span class="muted">Remove or change a filter above.</span></div>'
        : tab === 'shared'
          ? '<div class="empty">Nothing shared with you yet.<br><span class="muted">Open a portfolio link from another account to connect.</span></div>'
          : '<div class="empty">No properties yet.<br><span class="muted">Use the add box above.</span></div>';
      return;
    }
    // Stopped partners stay visible as a note (their properties are now
    // unreadable — the member docs were cut at revoke).
    const stoppedNote = tab !== 'mine' && stoppedPartners.length
      ? `<div class="muted" style="padding:10px 20px;border-bottom:1px solid var(--border)">${stoppedPartners
          .map((p) => `<span class="pill">${escapeHtml(partnerNames[p.id] || 'A partner')}</span> stopped sharing — their portfolio is hidden.`)
          .join(' ')}</div>`
      : '';
    listEl.innerHTML = stoppedNote + visible
      .map((p) => {
        const c = propertyStats(p, notesByPid.get(p.id) || null, baseline);
        const newFiles = firstVisit ? 0 : c.newFiles;
        const newComments = firstVisit ? 0 : c.newComments;
        // Creator chip on shared cards — denormalized owner_name (S2+), the
        // linked users/{owner} read for older properties.
        const creator = p.owner_uid !== user.uid ? (p.owner_name || partnerNames[p.owner_uid] || '') : '';
        // The two most recently added attributes (insertion order of the map
        // = add order) — desktop only, hidden on mobile by CSS.
        const recentAttrs = recentAttributes(p.user_attributes, 2);
        return `<div class="prop-row" data-href="#/map?pid=${p.id}">
          ${p.metadata?.image_url
            ? `<img class="thumb" src="${escapeAttr(p.metadata.image_url)}" alt="" />`
            : (typeof p.lat === 'number' && typeof p.lng === 'number')
              ? `<div class="thumb thumb-map" data-lat="${p.lat}" data-lng="${p.lng}" title="Neighbourhood map"></div>`
              : '<div class="thumb"></div>'}
          <span class="prop-body">
            <a class="title" href="#/map?pid=${p.id}">${escapeHtml(p.title || 'Untitled')}</a>
            ${p.source_url ? `<span class="sub">${sourceLinkHtml(p.source_url)}</span>` : ''}
            ${recentAttrs.length ? `<span class="recent-attrs" title="Most recent attributes">${recentAttrs
              .map(([n, v]) => `${escapeHtml(displayLabel(n))}: ${escapeHtml(displayValue(v))}`)
              .join(' · ')}</span>` : ''}
          </span>
          <span class="prop-meta">
            ${sortValueHtml(p, c)}
            ${statHtml(COMMENT_ICON, c.comments, newComments, 'comments')}
            ${statHtml(FILE_ICON, c.files, newFiles, 'property files')}
            ${statHtml(ATTRS_ICON, Object.keys(p.user_attributes || {}).length, 0, 'attributes')}
            ${creator ? `<span class="pill" title="Created by ${escapeAttr(creator)}">Created by ${escapeHtml(creator)}</span>` : ''}
            <span class="pill ${escapeAttr(p.status)}">${STATUS_LABELS[p.status] || p.status}</span>
          </span>
        </div>`;
      })
      .join('');
    watchMapThumbs(listEl);
  }

  // Whole row opens the property; inner anchors handle themselves (the title
  // link goes to the same page, the source link opens externally).
  listEl.addEventListener('click', (e) => {
    if (e.target.closest('a')) return;
    const row = e.target.closest('.prop-row');
    if (row?.dataset.href) navigate(row.dataset.href);
  });

  // Mine/Shared tabs — the merged list is the default; the tabs filter it.
  const tabsEl = el.querySelector('#dash-tabs');
  tabsEl?.addEventListener('click', (e) => {
    const b = e.target.closest('.tab');
    if (!b) return;
    tab = b.dataset.tab;
    tabsEl.querySelectorAll('.tab').forEach((t) => t.classList.toggle('active', t === b));
    renderList();
  });

  renderFilterBar();
  renderList();

  // Attribute lens: defs feed the filter options; seed defaults on first use.
  migrateAttributeStyle(user.uid).catch(() => {});
  ensureDefaultAttributes(user.uid).catch(() => {});
  function refreshList() {
    props = [...myProps, ...partnerProps];
    renderFilterBar(); // attribute usage counts follow the property list
    renderSortSelect();
    renderList();
    syncNoteWatchers();
  }
  const unsubProps = watchMyProperties(user.uid, (next) => {
    myProps = next;
    refreshList();
  });
  // Account sharing: partner profiles (provable array-contains query) →
  // their property docs (member-granted reads) merge into the list. An
  // ArrayUnion bump on a partner's property_ids fires this watch, so future
  // properties appear without re-sharing.
  const unsubShares = watchAccountShares(user.uid, async (docs) => {
    stoppedPartners = docs.filter((d) => d.active === false);
    const active = docs.filter((d) => d.active === true);
    const pids = active.flatMap((d) => d.property_ids || []);
    const [nextProps, names] = await Promise.all([
      getPropertiesByIds(pids),
      accountMemberNames(active.map((d) => d.id)),
    ]);
    partnerProps = nextProps;
    partnerNames = names;
    // Stopped partners' users docs are unreadable (the linked read needs an
    // active share) — take names from their (public) link docs instead.
    for (const p of stoppedPartners) {
      if (!p.link_token) continue;
      try {
        const l = await getAccountShareLink(p.link_token);
        if (l?.owner_name) partnerNames[p.id] = l.owner_name;
      } catch {
        /* keep the fallback */
      }
    }
    refreshList();
  });
  unsubAttrs = watchAttributes(user.uid, (defs) => {
    attrDefs = defs;
    renderSortSelect(); // attribute options follow the definitions
    renderFilterBar();
    renderList();
  });

  // Mobile: paste/add/download live inside a + FAB (top right); tapping it
  // expands the panel. Desktop keeps the inline row (FAB hidden by CSS).
  const fab = el.querySelector('#fab');
  const dashActions = el.querySelector('#dash-actions');
  let fabCleanup = null;
  if (fab && dashActions) {
    // Sit clear of the guest banner's dismiss button when it's shown.
    el.style.setProperty('--fab-top', el.querySelector('#guest-banner') ? '106px' : '60px');
    const setFabOpen = (open) => {
      dashActions.classList.toggle('open', open);
      fab.setAttribute('aria-expanded', String(open));
      fab.innerHTML = open ? FAB_CLOSE : FAB_PLUS;
      if (open) el.querySelector('#add-input')?.focus();
    };
    fab.addEventListener('click', (e) => {
      setFabOpen(!dashActions.classList.contains('open'));
      // The +/× icon swap detaches the original click target mid-event; the
      // document outside-click handler would then see it as "outside" and
      // instantly close the panel. Handle the click here only.
      e.stopPropagation();
    });
    const onDocClick = (e) => {
      if (dashActions.classList.contains('open') && !dashActions.contains(e.target) && !fab.contains(e.target)) {
        setFabOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setFabOpen(false);
    };
    document.addEventListener('click', onDocClick);
    document.addEventListener('keydown', onKey);
    fabCleanup = () => {
      document.removeEventListener('click', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }

  // ── map thumbnails — one shared hidden maplibre map renders them lazily ──
  // Only visible rows render (IntersectionObserver); results are cached in
  // the renderer, so re-renders of the list are free.
  let thumbObserver = null;
  const thumbWatched = new Set();
  function watchMapThumbs(rootEl) {
    if (!thumbObserver) {
      thumbObserver = new IntersectionObserver(
        (entries) => {
          for (const en of entries) {
            if (!en.isIntersecting) continue;
            const el = en.target;
            thumbObserver.unobserve(el);
            thumbWatched.delete(el);
            const lat = Number(el.dataset.lat);
            const lng = Number(el.dataset.lng);
            if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
            import('./map.js')
              .then((m) => m.renderMapThumb(lat, lng))
              .then((url) => {
                el.style.backgroundImage = `url("${url}")`;
              })
              .catch((e) => console.warn('map thumb failed', e));
          }
        },
        { rootMargin: '300px' }, // pre-render just-off-screen rows
      );
    }
    for (const el of thumbWatched) thumbObserver.unobserve(el);
    thumbWatched.clear();
    rootEl.querySelectorAll('.thumb-map').forEach((el) => {
      thumbObserver.observe(el);
      thumbWatched.add(el);
    });
  }

  window.addEventListener('pagehide', saveBaseline);

  return () => {
    window.removeEventListener('pagehide', saveBaseline);
    if (thumbObserver) for (const el of thumbWatched) thumbObserver.unobserve(el);
    thumbWatched.clear();
    if (fabCleanup) fabCleanup();
    unsubProps();
    if (unsubShares) unsubShares();
    if (unsubAttrs) unsubAttrs();
    for (const unsub of noteUnsubs.values()) unsub();
    saveBaseline();
  };
}
